// src/scenes/GroupCollectionAnalysis/index.js
import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Button,
  IconButton,
  Divider,
  List,
  Menu,
  MenuItem,
  ListItem,
  ListItemText,
  ListItemButton,
  CircularProgress,
  Select,
  FormControl,
  InputLabel,
  TextField,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  Area,
} from "recharts";
import {
  Dashboard as DashboardIcon,
  Person as PersonIcon,
  AttachMoney as AttachMoneyIcon,
  Assessment as AssessmentIcon,
  Email as EmailIcon,
  Notifications as NotificationsIcon,
  Settings as SettingsIcon,
  People as PeopleIcon,
  MonetizationOn as MonetizationOnIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import FilterAltIcon from "@mui/icons-material/FilterAlt";
import { MoreVert } from "@mui/icons-material";
import { Link } from "react-router-dom";
import { fetchExpensesData } from "../../../src/data/expensesData";
import { companyManagementUrl, userManagementUrl } from "../../config";
import {
  CustomersCard,
  ExpensesCard,
  RevenueCard,
  RevenueTrend,
  SalesCard,
  SalesTrend,
  SoldProductsChart,
} from "../../data/Axios/queries";
import { graphqlQuery } from "../../data/Axios/DynamicService";
import { tokens } from "../../theme";
import { fetchJsonDynamic } from "../../data/DynamicServiceNew/jsondynamicservice";
import { LocalizationProvider, DatePicker } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import Joyride, { STATUS } from "react-joyride";
import { useTour } from "../../context/TourContext";

const base_url = companyManagementUrl.uri;

// Define tour steps for GroupCollectionAnalysis
const groupCollectionAnalysisTourSteps = [
  {
    target: ".group-collection-analysis-container",
    content: "Welcome to the Group Collection Analysis Dashboard! Monitor group-based loan collection performance with filters for regions, branches, sales reps, and date ranges.",
    placement: "center",
    disableBeacon: true,
  },
  {
    target: ".group-collection-region-filter",
    content: "Use the Region filter to view group collection data for a specific region. This is available for Admins, Regional Managers, and Branch Managers.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-collection-branch-filter",
    content: "Select a Branch to analyze group collection metrics specific to that branch.",
    placement: "bottom",
    condition: (props) => [1, 2, 3].includes(props.userRole),
  },
  {
    target: ".group-collection-sales-rep-filter",
    content: "Choose a Sales Rep (Field Officer) to monitor their group collection performance.",
    placement: "bottom",
    condition: (props) => [1, 2, 3, 4].includes(props.userRole),
  },
  {
    target: ".group-collection-clear-all",
    content: "Click 'Clear All' to reset all filters and view default group collection data.",
    placement: "bottom",
    condition: (props) => props.userRole !== 5,
  },
  {
    target: ".group-collection-time-range",
    content: "Select a time range (e.g., Last 7 Days, Custom) to view group collection trends over a specific period.",
    placement: "bottom",
  },
  {
    target: ".group-collection-trend-chart",
    content: "The Group Collection Trend chart shows key group metrics like Collected Amount, Loans Due, Arrears, and Collection Rate over time.",
    placement: "top",
  },
  {
    target: ".group-collection-today-metrics",
    content: "This card displays today’s group collection metrics, including Collection Rate, Total Pending Due, Amount Collected, and more.",
    placement: "top",
  },
];

const GroupCollectionAnalysis = () => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const colors = tokens(theme.palette.mode);
  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const user = parseInt(decodedToken.RoleGroupId);
  const [branchData, setBranchData] = useState([]);
  const [fieldOfficerfData, setFieldOfficerData] = useState([]);
  const [regionData, setRegionData] = useState([]);
  const [regionId, setRegionId] = useState(null);
  const [branchId, setBranchId] = useState(null);
  const [fieldOfficerId, setFieldOfficerId] = useState(null);
  const [dashboardData, setDashboardData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [timeRange, setTimeRange] = useState("last_7_days");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [collectionTrend, setCollectionTrend] = useState([]);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const circleSize = isSmallScreen ? 150 : 200;

  // Tour state
  const { isFirstTime, touredPages, markPageAsToured } = useTour();
  const [runTour, setRunTour] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const currentPath = window.location.pathname;

  useEffect(() => {
    if (isFirstTime && !touredPages.includes(currentPath) && !loading) {
      setShowModal(true);
    }
  }, [isFirstTime, touredPages, currentPath, loading]);

  const handleJoyrideCallback = (data) => {
    const { status } = data;
    if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      setRunTour(false);
      markPageAsToured(currentPath);
      setShowModal(false);
    }
  };

  const startTour = () => {
    setRunTour(true);
    setShowModal(false);
  };

  const cancelTour = () => {
    setShowModal(false);
    markPageAsToured(currentPath);
  };

  // Fetch data on component mount
  useEffect(() => {
    async function fetchAllData() {
      try {
        setLoading(true);
        // Fetch regions data
        const regionDataResponse = await fetchJsonDynamic("region", null, null, base_url);
        if (regionDataResponse?.results) {
          const results = JSON.parse(regionDataResponse.results);
          setRegionData(results || []);
        }

        // Fetch branches data
        const branchDataResponse = await fetchJsonDynamic("branch", null, null, base_url);
        if (branchDataResponse?.results) {
          const results = JSON.parse(branchDataResponse.results);
          setBranchData(results || []);
        }

        // Fetch field officers data (users with Role Group 5)
        const fieldOfficerDataResponse = await fetchJsonDynamic(
          "allUsersView",
          null,
          null,
          userManagementUrl.uri
        );
        if (fieldOfficerDataResponse?.results) {
          const results = JSON.parse(fieldOfficerDataResponse.results);
          const fieldOfficersWithRole5 = results.filter((user) => user.RoleGroupId === 5);
          setFieldOfficerData(fieldOfficersWithRole5 || []);
        }
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setLoading(false);
      }
    }

    fetchAllData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "groupDashboardMetricsById",
        "dashboardMetrics",
        {
          companyid: null,
          regionid: regionId,
          branchid: branchId,
          fieldofficerid: fieldOfficerId,
        },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const results = JSON.parse(dashboardDataResponse.results);
        setDashboardData(results || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  const fetchData = async () => {
    try {
      const dashboardDataResponse = await fetchJsonDynamic(
        "groupCollectionTrendById",
        "dashboardMetrics",
        {
          companyId: null,
          regionId: regionId,
          branchId: branchId,
          fieldOfficerId: fieldOfficerId,
          dateRange: timeRange,
          customStartDate: startDate ? startDate.format("YYYY-MM-DD") : null,
          customEndDate: endDate ? endDate.format("YYYY-MM-DD") : null,
        },
        base_url
      );
      if (dashboardDataResponse?.results) {
        const data = JSON.parse(dashboardDataResponse.results);
        setCollectionTrend(data || []);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, [regionId, branchId, fieldOfficerId]);

  useEffect(() => {
    fetchData();
  }, [regionId, branchId, fieldOfficerId, timeRange, startDate, endDate]);

  const handleTimeRangeChange = (event) => {
    const selectedRange = event.target.value;
    setTimeRange(selectedRange);
    if (selectedRange !== "custom") {
      setStartDate(null);
      setEndDate(null);
    }
  };

  const paperStyle = {
    p: 1,
    display: "flex",
    alignItems: "center",
    height: 50,
    backgroundColor: `${colors.primary[400]} !important`,
  };

  const selectStyle = {
    height: "100%",
    "& .MuiSelect-select": {
      display: "flex",
      alignItems: "center",
      padding: "8px",
    },
  };

  const labelStyle = {
    display: "flex",
    alignItems: "center",
    backgroundColor: `${colors.primary[500]} !important`,
    color: "white",
    padding: "0 10px",
    borderRadius: "4px",
    height: "100%",
    marginRight: "10px",
  };

  const regionOptions = regionData.map((region) => ({
    value: region.Id,
    label: region.Name,
  }));

  const branchOptions = branchData
    .filter((branch) => !regionId || branch.RegionId === regionId)
    .map((branch) => ({
      value: branch.Id,
      label: branch.Name,
    }));

  const fieldOfficerOptions = fieldOfficerfData
    .filter((officer) => !branchId || officer.BranchId === branchId)
    .map((officer) => ({
      value: officer.Id,
      label: `${officer.FirstName} ${officer.LastName}`,
    }));

  const handleRegionChange = (event) => {
    const selectedRegionId = event.target.value;
    setRegionId(selectedRegionId);
    setBranchId(null);
    setFieldOfficerId(null);
  };

  const handleBranchChange = (event) => {
    const selectedBranchId = event.target.value;
    setBranchId(selectedBranchId);
    setFieldOfficerId(null);
  };

  const handleFieldOfficerChange = (event) => {
    const selectedFieldOfficerId = event.target.value;
    setFieldOfficerId(selectedFieldOfficerId);
  };

  const handleClearAll = () => {
    setRegionId(null);
    setBranchId(null);
    setFieldOfficerId(null);
    setStartDate(null);
    setEndDate(null);
    fetchDashboardData();
    fetchData();
  };

  const getPositiveProgressColor = (value) => {
    if (value <= 40) return "red";
    if (value <= 60) return "#d4a106";
    return "green";
  };

  return (
    <Box
      sx={{
        p: 3,
        display: "grid",
        gridTemplateColumns: "1fr",
        gridGap: "16px",
      }}
      className="group-collection-analysis-container"
    >
      {/* Joyride Tour */}
      <Joyride
        steps={groupCollectionAnalysisTourSteps.filter((step) =>
          step.condition ? step.condition({ userRole: user }) : true
        )}
        run={runTour}
        continuous
        showProgress
        showSkipButton
        callback={handleJoyrideCallback}
        styles={{
          options: {
            primaryColor: colors.blueAccent[500],
            backgroundColor: colors.primary[400],
            textColor: colors.grey[100],
            zIndex: 1000,
          },
        }}
      />

      {/* First-Time Modal */}
      <Dialog open={showModal} onClose={cancelTour}>
        <DialogTitle>Welcome to the Group Collection Analysis Dashboard!</DialogTitle>
        <DialogContent>
          This dashboard helps you monitor group-based loan collection performance. Would you like a guided tour to explore its features?
        </DialogContent>
        <DialogActions>
          <Button onClick={cancelTour}>Skip</Button>
          <Button onClick={startTour} variant="contained">
            Start Tour
          </Button>
        </DialogActions>
      </Dialog>

      {loading ? (
        <Typography>Loading...</Typography>
      ) : (
        <Grid container spacing={4}>
          {/* Filters */}
          <Grid container spacing={4} className="group-collection-filters">
            {(user === 1 || user === 2 || user === 3) && regionData.length > 0 && (
              <Grid item xs={12} md={3} className="group-collection-region-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Region
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Region...</InputLabel>
                    <Select
                      label="Select Region..."
                      value={regionId || ""}
                      onChange={handleRegionChange}
                      sx={selectStyle}
                      className="group-collection-region-select"
                      aria-label="Region Filter"
                    >
                      {regionOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {(user === 1 || user === 2 || user === 3) && branchData.length > 0 && (
              <Grid item xs={12} md={4} className="group-collection-branch-filter">
                <Paper elevation={3} sx={paperStyle}>
                  <Typography sx={labelStyle}>
                    <FilterAltIcon style={{ marginRight: "5px" }} />
                    Branches
                  </Typography>
                  <FormControl sx={{ flex: 1, height: "100%" }}>
                    <InputLabel>Select Branch...</InputLabel>
                    <Select
                      label="Select Branch..."
                      value={branchId || ""}
                      onChange={handleBranchChange}
                      sx={selectStyle}
                      className="group-collection-branch-select"
                      aria-label="Branch Filter"
                    >
                      {branchOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          {option.label}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Paper>
              </Grid>
            )}

            {(user === 1 || user === 2 || user === 3 || user === 4) &&
              fieldOfficerfData.length > 0 && (
                <Grid item xs={12} md={4} className="group-collection-sales-rep-filter">
                  <Paper elevation={3} sx={paperStyle}>
                    <Typography sx={labelStyle}>
                      <FilterAltIcon style={{ marginRight: "5px" }} />
                      Sales Reps
                    </Typography>
                    <FormControl sx={{ flex: 1, height: "100%" }}>
                      <InputLabel>Select Sales Rep...</InputLabel>
                      <Select
                        label="Select Sales Rep..."
                        value={fieldOfficerId || ""} // Fixed: Changed from regionId to fieldOfficerId
                        onChange={handleFieldOfficerChange}
                        sx={selectStyle}
                        className="group-collection-sales-rep-select"
                        aria-label="Sales Rep Filter"
                      >
                        {fieldOfficerOptions.map((option) => (
                          <MenuItem key={option.value} value={option.value}>
                            {option.label}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Paper>
                </Grid>
              )}

            {user !== 5 && (
              <Grid item xs={12} md={1} className="group-collection-clear-all">
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleClearAll}
                >
                  Clear All
                </Button>
              </Grid>
            )}
          </Grid>

          {/* Group Collection Trend Card */}
          <Grid item xs={12} md={8} className="group-collection-trend-chart">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 580,
                background: `${colors.primary[400]} !important`,
              }}
              role="region"
              aria-label="Group Collection Trend Chart"
            >
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <Box className="group-collection-time-range">
                  <Typography variant="h6" gutterBottom>
                    {timeRange.replace("_", " ").toUpperCase()} Group Collection Trend
                  </Typography>
                  <Select
                    value={timeRange}
                    onChange={handleTimeRangeChange}
                    sx={{ mb: 2 }}
                    className="group-collection-time-range-select"
                    aria-label="Time Range Selector"
                  >
                    <MenuItem value="last_7_days">Last 7 Days</MenuItem>
                    <MenuItem value="last_30_days">Last 30 Days</MenuItem>
                    <MenuItem value="this_month">This Month</MenuItem>
                    <MenuItem value="custom">Custom Range</MenuItem>
                  </Select>

                  {timeRange === "custom" && (
                    <Box sx={{ display: "flex", gap: 2, mt: 2 }} className="group-collection-custom-dates">
                      <DatePicker
                        label="Start Date"
                        value={startDate}
                        onChange={(newValue) => setStartDate(newValue ? dayjs(newValue) : null)}
                        renderInput={(params) => <TextField {...params} />}
                        className="group-collection-start-date"
                        aria-label="Start Date Picker"
                      />
                      <DatePicker
                        label="End Date"
                        value={endDate}
                        onChange={(newValue) => setEndDate(newValue ? dayjs(newValue) : null)}
                        renderInput={(params) => <TextField {...params} />}
                        className="group-collection-end-date"
                        aria-label="End Date Picker"
                      />
                    </Box>
                  )}
                </Box>
              </LocalizationProvider>

              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={collectionTrend}>
                  <defs>
                    <linearGradient id="GroupCollectedToday" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8884d8" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#8884d8" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="GroupLoansDueToday" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#ffc658" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#ffc658" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="GroupTotalArrears" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#d84e47" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#d84e47" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="GroupArrearsCollectedToday" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#4e73df" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#4e73df" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="GroupCollectionRateTodayPercentage" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#82ca9d" stopOpacity={0.8} />
                      <stop offset="95%" stopColor="#82ca9d" stopOpacity={0} />
                    </linearGradient>
                  </defs>

                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="PeriodLabel"
                    label={{
                      value: "Time",
                      position: "insideBottom",
                      offset: -5,
                    }}
                  />
                  <YAxis
                    yAxisId="left"
                    label={{
                      value: "Amount Collected",
                      angle: -90,
                      position: "insideLeft",
                    }}
                  />
                  <YAxis
                    yAxisId="right"
                    orientation="right"
                    label={{
                      value: "Collection Rate (%)",
                      angle: -90,
                      position: "insideRight",
                    }}
                  />
                  <Tooltip />
                  <Legend />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="GroupCollectedToday"
                    stroke="#8884d8"
                    name="Amount Collected"
                    fillOpacity={1}
                    fill="url(#GroupCollectedToday)"
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="GroupLoansDueToday"
                    stroke="#ffc658"
                    name="Loans Due"
                    fillOpacity={1}
                    fill="url(#GroupLoansDueToday)"
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="GroupTotalArrears"
                    stroke="#d84e47"
                    name="Total Arrears"
                    fillOpacity={1}
                    fill="url(#GroupTotalArrears)"
                  />
                  <Area
                    yAxisId="left"
                    type="monotone"
                    dataKey="GroupArrearsCollectedToday"
                    stroke="#4e73df"
                    name="Arrears Collected"
                    fillOpacity={1}
                    fill="url(#GroupArrearsCollectedToday)"
                  />
                  <Area
                    yAxisId="right"
                    type="monotone"
                    dataKey="GroupCollectionRateTodayPercentage"
                    stroke="#82ca9d"
                    name="Collection Rate (%)"
                    fillOpacity={1}
                    fill="url(#GroupCollectionRateTodayPercentage)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </Paper>
          </Grid>

          {/* Today’s Group Collection Metrics Card */}
          <Grid item xs={12} md={4} className="group-collection-today-metrics">
            <Paper
              elevation={3}
              sx={{
                p: 2,
                height: 580,
                background: `${colors.primary[400]} !important`,
                display: "flex",
                flexDirection: "column",
              }}
              role="region"
              aria-label="Today’s Group Collection Metrics"
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  mb: 2,
                }}
              >
                <Box
                  sx={{
                    background: theme.palette.primary.main,
                    color: "white",
                    px: 2,
                    py: 1,
                    borderRadius: "0px 10px 10px 0px",
                    flexShrink: 0,
                  }}
                >
                  <Typography variant="h6">Today</Typography>
                </Box>
                <Box
                  sx={{
                    flexGrow: 1,
                    borderBottom: `2px solid ${theme.palette.primary.main}`,
                    ml: 2,
                  }}
                />
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: 2,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "center",
                    alignItems: "center",
                    width: "100%",
                    mb: isSmallScreen ? 2 : 0,
                  }}
                >
                  <Box sx={{ position: "relative", display: "inline-flex" }}>
                    <CircularProgress
                      variant="determinate"
                      value={100}
                      size={circleSize}
                      thickness={7}
                      sx={{
                        color: "rgba(255, 255, 255, 0.4)",
                        position: "absolute",
                      }}
                    />
                    <CircularProgress
                      variant="determinate"
                      value={
                        parseFloat(
                          dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)
                        ) || 0
                      }
                      size={circleSize}
                      thickness={7}
                      sx={{
                        color: getPositiveProgressColor(
                          parseFloat(
                            dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)
                          ) || 0
                        ),
                      }}
                    />
                    <Box
                      sx={{
                        top: 0,
                        left: 0,
                        bottom: 0,
                        right: 0,
                        position: "absolute",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexDirection: "column",
                      }}
                    >
                      <Typography
                        variant="h5"
                        sx={{
                          color: getPositiveProgressColor(
                            parseFloat(
                              dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)
                            ) || 0
                          ),
                        }}
                      >
                        Collection Rate
                      </Typography>
                      <Typography
                        variant="h4"
                        component="div"
                        sx={{
                          color: getPositiveProgressColor(
                            parseFloat(
                              dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)
                            ) || 0
                          ),
                        }}
                      >
                        {parseFloat(
                          dashboardData[0]?.GroupCollectionRateTodayPercentage?.toFixed(2)
                        ) || 0}
                        %
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ width: "100%" }}>
                  <List sx={{ p: 0 }}>
                    <ListItemButton divider>
                      <ListItemText primary="Total Pending Due" />
                      <Typography
                        variant="h6"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          px: 1,
                          borderRadius: "5px",
                        }}
                      >
                        {dashboardData[0]?.GroupLoansDueToday || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Amount Collected" />
                      <Typography
                        variant="h6"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          px: 1,
                          borderRadius: "5px",
                        }}
                      >
                        {dashboardData[0]?.GroupCollectedToday || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Total Arrears" />
                      <Typography
                        variant="h6"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          px: 1,
                          borderRadius: "5px",
                        }}
                      >
                        {dashboardData[0]?.GroupTotalArrears || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Arrears Collected" />
                      <Typography
                        variant="h6"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          px: 1,
                          borderRadius: "5px",
                        }}
                      >
                        {dashboardData[0]?.GroupArrearsCollectedToday || 0}
                      </Typography>
                    </ListItemButton>
                    <ListItemButton divider>
                      <ListItemText primary="Prepayments Collected" />
                      <Typography
                        variant="h6"
                        sx={{
                          backgroundColor: theme.palette.primary.main,
                          color: "white",
                          px: 1,
                          borderRadius: "5px",
                        }}
                      >
                        {dashboardData[0]?.GroupPrepaidLoansToday || 0}
                      </Typography>
                    </ListItemButton>
                  </List>
                </Box>
              </Box>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
};

export default GroupCollectionAnalysis;